<template>
  <DetailCard>
    <template #heading>
      <span class="pi pi-images"></span>
      {{ $t(titleKey) }}
    </template>
    <template #controls>
      <FileUpload
          ref="fileUpload"
          :data-testid="`photo-upload-${photoType}`"
          name="files[]"
          url="/photo"
          accept="image/*"
          :max-file-size="100000000"
          :file-limit="maxPhotos"
          :multiple="maxPhotos !== 1"
          :disabled="isFull || uploading"
          :auto="true"
          :show-cancel-button="false"
          :show-upload-button="false"
          custom-upload
          @uploader="upload"
          :dt="{
            border: {
              color: 'transparent',
            },
            background: 'transparent',
            header: {
              padding: '0px',
            },
            content: {
              padding: '0px',
            },
          }"
      >
        <template #content="{ messages }">
          <div v-if="messages && messages.length > 0" class="p-2">
            <Message v-for="msg of messages" :key="msg" severity="error" :closable="false">{{ msg }}</Message>
          </div>
        </template>
      </FileUpload>
    </template>
    <div v-if="uploading" class="px-4 pb-2" data-testid="photo-upload-progress">
      <ProgressBar :value="uploadProgress" :show-value="true">
        {{ uploadedCount }} / {{ uploadTotal }}
      </ProgressBar>
    </div>
    <div v-if="photos.length > 0">
      <VueDraggable v-model="photos" @end="onSortEnd" :disabled="uploading" :data-testid="`photo-gallery-${photoType}`" class="gallery flex flex-wrap justify-center gap-4 p-4" handle=".drag-handle">
        <Card
          :data-id="photo.id"
          v-for="photo in photos"
          :key="photo.id"
          :img-src="photo.url"
          :img-alt="photo.name"
          body-class="photo-input-text"
          title-tag="h6"
          img-top
          :dt="{
            body: {
              padding: '0px !important',
            },
          }"
          class="card"
        >
          <template #header>
            <div class="relative">
              <div v-if="canReorder" class="absolute top-2 left-2">
                <Button class="drag-handle" size="sm" severity="secondary" tabindex="-1">
                  <span class="pi pi-arrows-alt"></span>
                </Button>
              </div>
              <div class="absolute top-2 right-2">
                <Button
                  class="photo-delete-btn"
                  size="sm"
                  severity="danger"
                  tabindex="-1"
                  @click.stop.prevent="deletePhoto(photo.id)"
                >
                  <span class="pi pi-times"></span>
                </Button>
              </div>
              <img :src="photo.url" :alt="photo.name" class="card-img-top" />
            </div>
          </template>
          <template #content>
            <div class="p-2">
              <InputText
                @keyup.enter="updateDescription(photo)"
                @blur="updateDescription(photo)"
                v-model="photo.description"
                fluid
                :placeholder="$t(`messages.detail.photos.description`)"
              />
            </div>
          </template>
        </Card>
      </VueDraggable>
    </div>
  </DetailCard>
</template>

<script setup>
import { computed, onMounted, ref, shallowRef } from 'vue'
import { useToast } from 'primevue/usetoast'
import { useI18n } from 'vue-i18n'
import { VueDraggable } from 'vue-draggable-plus'
import DetailCard from './detail/DetailCard.vue'

const toast = useToast()
const { t } = useI18n()

const props = defineProps({
  objectId: { type: [String, Number], default: '' },
  photoType: { type: String, default: '' },
  titleKey: { type: String, default: 'messages.detail.photos.title' },
  maxPhotos: { type: Number, default: null },
})

const emit = defineEmits(['photos-changed'])

const fileUpload = ref(null)

const loading = shallowRef(false)
const uploading = shallowRef(false)
const uploadProgress = shallowRef(0)
const uploadedCount = shallowRef(0)
const uploadTotal = shallowRef(0)
const photos = ref([])

const isFull = computed(() => props.maxPhotos != null && photos.value.length >= props.maxPhotos)
// RED-334: no drag while an upload runs: every chunk reloads the list, which
// would throw a half-finished drag back to the saved order.
const canReorder = computed(() => props.maxPhotos !== 1 && photos.value.length > 1 && !uploading.value)

onMounted(() => {
  loadPhotos()
})

const loadPhotos = async () => {
  loading.value = true
  await axios
    .get(route('photo.index', { objectId: props.objectId, photoType: props.photoType }))
    .then(response => {
      photos.value = response.data
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: 'Fotode laadimine ebaõnnestus', life: 5000 })
    })
    .finally(() => {
      loading.value = false
      emit('photos-changed', photos.value.length)
    })
}

const onSortEnd = () => {
  const ids = photos.value.map(photo => photo.id)
  reorderPhotos(ids)
}

/**
 * Files per POST. The server accepts up to 40 per request, but PHP's default
 * max_file_uploads is 20 and a phone-camera batch of 40 is easily 200 MB —
 * small chunks keep every request well inside both limits and let the
 * gallery grow while a large selection is still uploading (RED-334).
 */
const UPLOAD_CHUNK_SIZE = 10

const postChunk = async (files, doneBefore, total) => {
  const formData = new FormData()
  formData.append('objectId', props.objectId)
  formData.append('photoType', props.photoType)
  for (const file of files) {
    formData.append('files[]', file)
  }

  await axios.post(route('photo.upload'), formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (progressEvent) => {
      if (progressEvent.total) {
        const chunkShare = (progressEvent.loaded / progressEvent.total) * files.length
        uploadProgress.value = Math.round(((doneBefore + chunkShare) * 100) / total)
      }
    },
  })
}

const upload = async (e) => {
  const files = Array.from(e.files)
  uploading.value = true
  uploadProgress.value = 0
  uploadedCount.value = 0
  uploadTotal.value = files.length

  try {
    for (let start = 0; start < files.length; start += UPLOAD_CHUNK_SIZE) {
      const chunk = files.slice(start, start + UPLOAD_CHUNK_SIZE)
      await postChunk(chunk, start, files.length)
      uploadedCount.value = start + chunk.length
      uploadProgress.value = Math.round((uploadedCount.value * 100) / files.length)
      await loadPhotos()
    }
  } catch (err) {
    const status = err?.response?.status
    const body = err?.response?.data
    console.error('Photo upload failed', { status, body, error: err })

    let summary
    if (status === 419) {
      summary = t('messages.detail.photos.errors.csrfExpired')
    } else if (status === 413) {
      summary = t('messages.detail.photos.errors.tooLarge')
    } else if (status === 422) {
      summary = body?.message || t('messages.detail.photos.errors.generic')
    } else {
      summary = t('messages.detail.photos.errors.generic')
    }

    toast.add({ severity: 'error', summary, life: 7000 })
  } finally {
    // Always drop the pending selection: photos already posted are persisted
    // and shown in the gallery, and the uploader must never stay stuck.
    fileUpload.value?.clear()
    uploading.value = false
    uploadProgress.value = 0
  }
}

const updateDescription = (photo) => {
  if (photo.description === '') {
    return
  }

  loading.value = true
  axios
    .put(route('photo.update', { photoId: photo.id }), { description: photo.description })
    .catch(() => {
      toast.add({ severity: 'error', summary: 'Kirjelduse uuendamine ebaõnnestus', life: 5000 })
    })
    .finally(() => {
      loading.value = false
    })
}

const deletePhoto = (id) => {
  loading.value = true
  axios
    .delete(route('photo.delete', { photoId: id }))
    .then(() => {
      loadPhotos()
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: 'Foto kustutamine ebaõnnestus', life: 5000 })
    })
    .finally(() => {
      loading.value = false
    })
}

// RED-334: one reorder PUT at a time, in drag order, so two quick drags can
// never land on the server the wrong way round. Each PUT carries the whole
// list, so the last one wins. A failed one reloads the saved order: the screen
// never shows an order the Word export will not print.
let reorderQueue = Promise.resolve()

const reorderPhotos = (ids) => {
  loading.value = true
  reorderQueue = reorderQueue
    .then(() => axios.put(
      route('photo.reorder', { objectId: props.objectId, photoType: props.photoType }),
      { order: ids },
    ))
    .catch(() => {
      toast.add({ severity: 'error', summary: 'Fotode järjestamine ebaõnnestus', life: 5000 })
      return loadPhotos()
    })
    .finally(() => {
      loading.value = false
    })
}
</script>

<style scoped>
.card-img-top {
  width: 100%;
  height: 15vw;
  object-fit: cover;
}

.card {
  flex-basis: 328px;
  max-width: calc(50% - 0.5rem);
}
</style>
